import type {
  OrderedFlight,
  TrackingProperties,
} from "@b2bportal/air-booking-api";
import {
  type CipherText,
  type ErrorCode,
  type FulfillFailure,
  type FulfillResponse,
  FulfillResponseEnum,
  type FulfillSuccess,
  type PaymentOpaqueValue,
  Product,
  type ProductOpaqueValue,
} from "@b2bportal/purchase-api";
import { pollFulfill, trackEvent } from "@hopper-b2b/api";
import {
  AirCfarSelectors,
  AirChfarSelectors,
  AirDisruptionSelectors,
  AirPFExerciseSelectors,
  CartFulfillErrorModal,
  FlightPassengerSelectors,
  FlightSelectors,
} from "@hopper-b2b/checkout";
import {
  COMPLETE_BUY_AIR,
  type ChooseTravelerProperties,
  type CompleteBuyAirCfarChoices,
  type CompleteBuyAirChfarChoices,
  type CompleteBuyAirProperties,
  type ISelectedTrip,
  type TripDetails,
  getHopperFareRatingName,
} from "@hopper-b2b/types";
import {
  type ITrackingPropertiesRequest,
  PollingResult,
  PollingType,
  combineTrackingProperties,
  endPollingPerformance,
  startPollingPerformance,
} from "@hopper-b2b/utilities";
import dayjs from "dayjs";
import type { FlightMachineContext } from "../../../types";
import { getFulfillCipherText } from "../selectors";

export interface PollFulfillResult {
  products: Array<ProductOpaqueValue>;
  payments: Array<PaymentOpaqueValue>;
}

interface GetCompleteBuyAirTrackingProps {
  success: boolean;
  orderedFlight?: OrderedFlight;
  products?: Array<ProductOpaqueValue>;
  additionalTrackingProperties?: TrackingProperties;
  failure_reason?: string;
  multiTicketType?: "single" | "hacker_fare" | "virtual_interline";
}

export const pollFulfillService = (context: FlightMachineContext, _event) =>
  // eslint-disable-next-line no-async-promise-executor
  new Promise(async (resolve, reject) => {
    const fulFillCipherText = getFulfillCipherText({ context });
    const selectedPassengers =
      FlightPassengerSelectors.getAllSelectedUserPassengersParent({
        context,
      });
    const selectedTripDetails = FlightSelectors.getSelectedTripDetailsParent({
      context,
    });
    const selectedTrip = FlightSelectors.getSelectedTripParent({ context });
    const chooseTravelerProperties =
      FlightSelectors.getChooseTravelerProperties({
        context,
      });

    const completeBuyAirPriceFreezeProperties =
      AirPFExerciseSelectors.getCompleteBuyAirPriceFreezeProperties({
        context,
      });

    const completeBuyAirCfarProperties =
      AirCfarSelectors.getCompleteBuyAirCfarProperties({
        context,
      });
    const completeBuyAirChfarProperties =
      AirChfarSelectors.getCompleteBuyAirChfarProperties({
        context,
      });
    const completeBuyAirCfarChoice =
      AirCfarSelectors.getCompleteBuyAirCfarChoice({ context });
    const completeBuyAirChfarChoice =
      AirChfarSelectors.getCompleteBuyAirChfarChoice({ context });

    const completeBuyDisruptionProperties =
      context.featureFlags.enableScheduleChange ||
      context.featureFlags.enableMissedConnection
        ? AirDisruptionSelectors.getDisruptionFulfillTrackingProperties({
            context,
          })
        : ({} as Record<string, never>);

    const getTrackingProperties = ({
      success,
      orderedFlight,
      failure_reason,
      products,
      additionalTrackingProperties,
      multiTicketType,
    }: GetCompleteBuyAirTrackingProps): ITrackingPropertiesRequest => {
      const productsTrackingProperties = products?.map((product) => {
        return product.value.trackingProperties;
      });
      return combineTrackingProperties(
        [
          completeBuyAirPriceFreezeProperties,
          completeBuyAirCfarProperties,
          completeBuyAirChfarProperties,
          additionalTrackingProperties,
          productsTrackingProperties,
        ].flat(),
        {
          ...getPartialCompleteBuyAirTrackingProperties(
            success,
            orderedFlight,
            failure_reason,
            chooseTravelerProperties,
            selectedTripDetails,
            selectedTrip,
            selectedPassengers.length,
            completeBuyAirCfarChoice,
            completeBuyAirChfarChoice,
            completeBuyDisruptionProperties.properties,
            fulFillCipherText,
            multiTicketType
          ),
        }
      );
    };

    try {
      if (!fulFillCipherText) {
        reject({ type: CartFulfillErrorModal.NoCipherText });
        return;
      }

      const delayTimes = [
        2000, 2000, 2000, 2000, 4000, 6000, 8000, 10000, 20000, 30000,
      ];
      let pollComplete = false;
      let index = 0;
      const startPerformance = startPollingPerformance();

      while (!pollComplete) {
        const currentDelayTime = delayTimes[index];
        if (index !== delayTimes.length - 1) {
          index++;
        }

        await new Promise((resolve) => setTimeout(resolve, currentDelayTime));

        const response: FulfillResponse = await pollFulfill(
          fulFillCipherText
        ).catch((e): any => {
          // [CRT-578] do not error out on network errors, instead retry
          if (e.response.status !== 200) {
            return Promise.resolve({
              FulfillResponse: FulfillResponseEnum.Pending,
            });
          }
          return Promise.reject(e);
        });

        switch (response.FulfillResponse) {
          case FulfillResponseEnum.Failure: {
            endPollingPerformance(
              startPerformance,
              PollingType.FULFILL,
              PollingResult.FAILURE
            );
            pollComplete = true;
            const failedResponse = response as FulfillFailure;
            trackEvent({
              eventName: COMPLETE_BUY_AIR,
              ...getTrackingProperties({
                failure_reason:
                  failedResponse.errors.length > 0
                    ? failedResponse.errors
                        .map((error) =>
                          (error as ErrorCode)?.code
                            ? (error as ErrorCode).code
                            : // TODO: fix type below in b2binterfaces
                              (error as any).Error || "ProductError"
                        )
                        .join(" - ")
                    : "Poll Finalized response returned an error and the given error code is not handled.",
                success: false,
              }),
            });
            window.parent.postMessage(
              {
                type: "METRIC",
                payload: {
                  metricType: COMPLETE_BUY_AIR,
                  payload: {
                    success: false,
                    failure_reason: "Poll Finalized response returned an error",
                  },
                },
              },
              import.meta.env["VITE_UBER_URL"]
            );
            if (failedResponse.errors.length > 0) {
              reject({
                type: CartFulfillErrorModal.FailedPolling,
                data: failedResponse.errors,
              });
            } else {
              reject({
                type: CartFulfillErrorModal.FailedPollingNoErrorCode,
              });
            }
            break;
          }
          case FulfillResponseEnum.Pending:
            continue;
          case FulfillResponseEnum.Success: {
            endPollingPerformance(
              startPerformance,
              PollingType.FULFILL,
              PollingResult.SUCCESS
            );
            pollComplete = true;
            const fulfillResponse = response as FulfillSuccess;
            const products = fulfillResponse.products;
            const payments = fulfillResponse.payments;
            const additionalTrackingProperties =
              fulfillResponse.trackingProperties;
            const flightProduct = products.find(
              (p) =>
                p.type === Product.MultiProviderFlight ||
                p.type === Product.Flight
            );
            const multiTicketType =
              flightProduct.type === Product.MultiProviderFlight
                ? "hacker_fare" //TODO: add "virtual_interline" when it's ready
                : "single";
            resolve({ products, payments });
            trackEvent({
              eventName: COMPLETE_BUY_AIR,
              ...getTrackingProperties({
                success: true,
                orderedFlight: flightProduct.value,
                products: products,
                additionalTrackingProperties: additionalTrackingProperties,
                multiTicketType,
              }),
            });

            window.parent.postMessage(
              {
                type: "METRIC",
                payload: {
                  metricType: COMPLETE_BUY_AIR,
                  payload: {
                    success: true,
                    orderedFlight: products.find(
                      (p) => p.type === Product.Flight
                    )?.value,
                  },
                },
              },
              import.meta.env["VITE_UBER_URL"]
            );
          }
        }
      }
    } catch (e) {
      trackEvent({
        eventName: COMPLETE_BUY_AIR,
        ...getTrackingProperties({
          success: false,
          failure_reason: "Poll Finalized Network Call Failed",
        }),
      });
      window.parent.postMessage(
        {
          type: "METRIC",
          payload: {
            metricType: COMPLETE_BUY_AIR,
            payload: {
              success: false,
              failure_reason: "Poll Finalized Network Call Failed",
            },
          },
        },
        import.meta.env["VITE_UBER_URL"]
      );

      reject(e);
    }
  });

const getPartialCompleteBuyAirTrackingProperties = (
  success,
  orderedFlight: OrderedFlight,
  failure_reason,
  chooseTravelerProperties: ChooseTravelerProperties,
  selectedTripDetails: TripDetails,
  selectedTrip: ISelectedTrip,
  pax_total: number,
  completeBuyAirCfarChoice: CompleteBuyAirCfarChoices,
  completeBuyAirChfarChoice: CompleteBuyAirChfarChoices,
  completeBuyDisruptionProperties:
    | {
        delay_protection_choice?: 0 | 1;
        missed_connection_choice?: 0 | 1;
        charged_delay_protection_total_usd: number;
        charged_missed_connection_total_usd: number;
      }
    | Record<string, never>,
  cipherText?: CipherText,
  multiTicketType?: "single" | "hacker_fare" | "virtual_interline"
): CompleteBuyAirProperties => {
  // const stops =
  //   selectedTripDetails.slices.length > 1
  //     ? selectedTripDetails.slices[0].stops +
  //       selectedTripDetails.slices[1].stops
  //     : selectedTripDetails.slices[0].stops;
  const fare = selectedTripDetails.fareDetails.find(
    (f) =>
      f.id === selectedTrip.outgoingFareId || f.id === selectedTrip.returnFareId
  );
  const carrier: string | undefined =
    fare &&
    fare.slices.length > 0 &&
    fare.slices[0].fareDetails.segments.length > 0
      ? fare?.slices[0].fareDetails.segments[0].validatingCarrierCode
      : undefined;

  const outgoingHackerFare =
    selectedTripDetails.fareDetails.find(
      (f) => f.id === selectedTrip.outgoingFareId
    )?.multiTicket || false;

  const returnHackerFare =
    selectedTripDetails.fareDetails.find(
      (f) => f.id === selectedTrip?.returnFareId
    )?.multiTicket || false;

  const properties: CompleteBuyAirProperties = {
    // stops, // ! Property is not supported by complete_buy_air event
    ...chooseTravelerProperties,
    success,
    booking_session_id: cipherText?.value || "",
    carrier: carrier || "",
    is_agent_session: false,
    pax_total,
    advance: `${dayjs(selectedTripDetails.slices[0].departureTime).diff(
      dayjs(),
      "day"
    )} days`,
    is_multi_ticket: outgoingHackerFare || returnHackerFare,
    cfar_choice: completeBuyAirCfarChoice,
    chfar_choice: completeBuyAirChfarChoice,
    multi_ticket_type: multiTicketType,
    ...completeBuyDisruptionProperties,
  };

  if (failure_reason) {
    properties.failure_reason = failure_reason;
  }

  if (!orderedFlight) return properties;

  const trip = orderedFlight.travelItinerary;
  const provider = trip?.locators?.agent.provider;
  return {
    ...properties,
    fare_class: fare
      ? getHopperFareRatingName(fare?.slices[0].fareShelf?.rating)
      : "",
    rewards_currency:
      orderedFlight.pricingSummary.totalPricing.total.fiat.currencyCode,
    provider,
  };
};
